import zod from 'zod'

import { PaymentMethodEnum, WeekDayEnum } from './enum.schemas'

export const signInSchema = zod.object({
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' }),
  password: zod
    .string()
    .min(1, { message: 'enter_password' })
    .min(8, { message: 'password_eight_characters' }),
})

export const forgotPasswordSchema = zod.object({
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' }),
})

export const resetPasswordSchema = zod
  .object({
    password: zod
      .string()
      .min(1, { message: 'enter_password' })
      .min(8, { message: 'password_eight_characters' }),
    confirm_password: zod
      .string()
      .min(1, { message: 'enter_password' })
      .min(8, { message: 'password_eight_characters' }),
  })
  .refine(data => data.password === data.confirm_password, {
    message: 'password_dont_match',
    path: ['confirm_password'],
  })

export const signUpSchema = zod.object({
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' }),
  password: zod
    .string()
    .min(1, { message: 'enter_password' })
    .min(8, { message: 'password_eight_characters' }),
  have_business_license: zod.boolean().default(false),
  business_license: zod.string().optional(),
})

export const signUpBusinessSchema = zod.object({
  name: zod.string().min(1, { message: 'empty_name' }),
  cnpj: zod.string().min(14, { message: 'empty_cnpj' }),
  phone: zod.string().min(11, { message: 'empty_phone' }),
  legal_representative: zod
    .string()
    .min(1, { message: 'empty_legal_representative' }),
  zipcode: zod.string().min(8, { message: 'empty_zipcode' }),
  street: zod.string().min(1, { message: 'empty_street' }),
  neighborhood: zod.string().min(1, { message: 'empty_neighborhood' }),
  number: zod.string().min(1, { message: 'empty_number' }),
  complement: zod.string().optional(),
})

export const PaymentMethodsSchema = zod.object({
  type: PaymentMethodEnum,
  description: zod.string(),
  icon: zod.string(),
  active: zod.boolean().default(false),
})

export const signUpConfigurationSchema = zod.object({
  parking_space: zod.number().min(1, { message: 'empty_parking_space' }),
  daily_price: zod.string().min(1, { message: 'empty_price' }),
  hour_price: zod.string().min(1, { message: 'empty_price' }),
  price_thirty_min: zod.string().min(1, { message: 'empty_price' }),
  discount_first_hour: zod.string().nullable(),
  monthly: zod.string().nullable(),
  same_time_every_day: zod.boolean().default(false),
  allow_scheduling: zod.boolean().default(false),
  premium: zod.boolean().default(false),
  business_hours: zod.array(
    zod.object({
      week_day: WeekDayEnum.default('MONDAY'),
      description: zod.string(),
      hour_start: zod.string().optional(),
      hour_end: zod.string().optional(),
      closed: zod.boolean().default(false),
    })
  ),
  payment_methods: zod.array(PaymentMethodsSchema),
})

export const signUpCharacteristicsSchema = zod.object({
  characteristics: zod.array(
    zod.object({
      id: zod.string(),
      name: zod.string(),
      disabled: zod.boolean().default(false),
    })
  ),
  services: zod.array(
    zod.object({
      id: zod.string(),
      name: zod.string(),
      price: zod.string(),
      disabled: zod.boolean().default(false),
    })
  ),
})

export const configurationSchema = signUpConfigurationSchema
  .merge(signUpCharacteristicsSchema)
  .merge(
    zod.object({
      name: zod.string().min(1, { message: 'empty_name' }),
      phone: zod.string().min(11, { message: 'empty_phone' }),
      legal_representative: zod
        .string()
        .min(1, { message: 'empty_legal_representative' }),
      zipcode: zod.string().min(8, { message: 'empty_zipcode' }),
      street: zod.string().min(1, { message: 'empty_street' }),
      neighborhood: zod.string().min(1, { message: 'empty_neighborhood' }),
      number: zod.string().min(1, { message: 'empty_number' }),
      complement: zod.string().optional(),
      premium: zod.boolean().default(false),
      allow_scheduling: zod.boolean().default(false),
      latitude: zod.string().min(1, { message: 'empty_latitude' }),
      longitude: zod.string().min(1, { message: 'empty_longitude' }),
    })
  )

export const bookingRefusedSchema = zod.object({
  full_parking_lot: zod.string().nullable().optional(),
  lack_employee: zod.string().nullable().optional(),
  outside_opening_hours: zod.string().nullable().optional(),
  others: zod.string().nullable().optional(),
  reason: zod.string().nullable().optional(),
})

export const selectSchema = zod.object(
  {
    label: zod.string(),
    value: zod.string(),
  },
  { required_error: 'empty_parking', invalid_type_error: 'invalid_parking' }
)

export const couponsSchema = zod.object({
  email: zod.string().nullable().optional(),
  parking: selectSchema,
  start_date: zod.date().optional(),
  expiration_date: zod.date().optional(),
  monetary: zod.string().nullable().optional(),
  percentual: zod.string().nullable().optional(),
  tag: zod.string().min(5, { message: 'empty_tag' }),
  value_discount: zod
    .string({ required_error: 'empty_value_discount' })
    .min(1, { message: 'empty_value_discount' }),
  max_value_discount: zod.string().optional(),
  max_quantity: zod.string().nullable().optional(),
})

export const monthlySchema = zod.object({
  name: zod.string().min(1, { message: 'empty_name_2' }),
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' }),
  phone: zod.string().min(11, { message: 'empty_phone' }),
  cpf_cnpj: zod.string().min(11, { message: 'empty_cpf_cnpj' }),
  zipcode: zod.string().min(8, { message: 'empty_zipcode' }),
  city: zod.string().min(1, { message: 'empty_city' }),
  state: zod.string().min(1, { message: 'empty_state' }),
  street: zod.string().min(1, { message: 'empty_street' }),
  neighborhood: zod.string().min(1, { message: 'empty_neighborhood' }),
  number: zod.string().min(1, { message: 'empty_number' }),
  country: zod.string().min(1, { message: 'empty_country' }),
  complement: zod.string().optional(),
  days: zod.array(WeekDayEnum, {
    required_error: 'empty_days',
  }),
  number_vacancies: zod.string().min(1, { message: 'empty_parking_space' }),
  type_payment: zod.object(
    {
      label: zod.string(),
      value: zod.string(),
    },
    { required_error: 'empty_select' }
  ),
  start_hours: zod.string().min(1, { message: 'empty_start_hours' }),
  end_hours: zod.string().min(1, { message: 'empty_end_hours' }),
  billing_date: zod.date({ invalid_type_error: 'empty_date' }),
  value: zod.string().min(1, { message: 'empty_price' }),
  maturity_fee: zod.string().optional(),
  due_date: zod.string().min(1, { message: 'empty_due_date' }),
  vehicle_license_plate: zod.string().optional(),
  iss_withheld: zod.string().default('0').optional(),
})

export const accreditedSchema = zod.object({
  name: zod.string().min(1, { message: 'empty_name_2' }),
  monthly_id: zod
    .object({
      label: zod.string(),
      value: zod.string(),
    })
    .optional(),
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' }),
  cpf: zod
    .string({ required_error: 'empty_cpf' })
    .min(1, { message: 'empty_cpf' }),
  phone: zod.string().min(11, { message: 'empty_phone' }),
  vehicle_type: zod.object(
    {
      label: zod.string(),
      value: zod.string(),
    },
    { required_error: 'empty_select' }
  ),
  vehicle_model: zod.string().min(1, { message: 'empty_vehicle_model' }),
  vehicle_license_plate: zod
    .string()
    .min(1, { message: 'empty_vehicle_license_plate' }),
  vehicle_collor: zod.string().min(1, { message: 'empty_vehicle_collor' }),
})

export const courtyardCheckInSchema = zod.object({
  name: zod.string().optional(),
  email: zod.string().optional(),
  phone: zod.string().optional(),
  checkin_invoice: zod.string(),
  vehicle_type: zod.string(),
  start_date: zod.date({ invalid_type_error: 'empty_date' }),
  start_hours: zod.string({ invalid_type_error: 'empty_date' }),
  follow_without_charging: zod.string().default('-1').optional(),
  vehicle_license_plate: zod
    .string()
    .min(1, { message: 'empty_vehicle_license_plate' }),
  vehicle_model: zod.string().optional(),
  accredited_id: zod
    .object(
      {
        label: zod.string(),
        value: zod.string(),
      },
      { required_error: 'empty_select' }
    )
    .optional(),
  parking_services: zod.array(
    zod
      .object({
        id: zod.string(),
        name: zod.string(),
        value: zod.string(),
        disabled: zod.boolean().default(false),
      })
      .optional()
  ),
})

export const operationChartSelectSchema = zod.object({
  month: zod
    .object(
      {
        label: zod.string(),
        value: zod.string(),
      },
      { required_error: 'empty_select' }
    ),
})


export const courtyardCheckOutSchema = zod.object({
  checkout_invoice: zod.string(),
  end_date: zod.date({ invalid_type_error: 'empty_date' }),
  end_hours: zod.string({ invalid_type_error: 'empty_date' }),
  type_payment: zod.object(
    {
      label: zod.string(),
      value: zod.string(),
    },
    { required_error: 'empty_select' }
  ),
})

export const createUserSchema = zod.object({
  name: zod.string().min(1, { message: 'empty_name_2' }),
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' }),
  cpf: zod
    .string({ required_error: 'empty_cpf' })
    .min(1, { message: 'empty_cpf' }),
  phone: zod.string().min(11, { message: 'empty_phone' }),
  password: zod
    .string()
    .min(1, { message: 'enter_password' })
    .min(8, { message: 'password_eight_characters' }),
  role: zod
    .object({
      label: zod.string(),
      value: zod.string(),
    }),
  parking_id: zod
    .object({
      label: zod.string(),
      value: zod.string(),
    })
    .optional(),
})

export const editUserSchema = zod.object({
  name: zod.string().min(1, { message: 'empty_name_2' }).optional(),
  email: zod
    .string()
    .min(1, { message: 'empty_email' })
    .email({ message: 'invalid_email' })
    .optional(),
  cpf: zod
    .string()
    .optional(),
  phone: zod.string().min(11, { message: 'empty_phone' }).optional(),
  role: zod
    .object({
      label: zod.string(),
      value: zod.string(),
    })
    .optional(),
  parking_id: zod
    .object({
      label: zod.string(),
      value: zod.string(),
    })
    .optional(),
})

